import { Book, Author, Bitcoiner } from '@/types';
import { fetchBooks, fetchAuthors, fetchPeople } from '@/lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function useBooks() {
  const { data = [], isLoading, error } = useQuery<Book[], Error>({
    queryKey: ['books'],
    queryFn: fetchBooks
  });

  return {
    data,
    isLoading,
    error
  };
}

export function useAuthors() {
  const queryClient = useQueryClient();

  const { data = [], isLoading, error } = useQuery<Author[], Error>({
    queryKey: ['authors'],
    queryFn: fetchAuthors,
    retry: 1
  });

  const createAuthor = useMutation({
    mutationFn: async (data: Omit<Author, "id" | "books">) => {
      const response = await fetch('/api/authors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['authors'] });
    }
  });

  return { data, isLoading, error, createAuthor };
}

export function usePeople() {
  const queryClient = useQueryClient();

  const { data = [], isLoading, error } = useQuery<Bitcoiner[], Error>({
    queryKey: ['people'],
    queryFn: fetchPeople
  });

  const updatePersonMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Bitcoiner> }) => {
      const response = await fetch(`/api/people/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
    }
  });

  const deletePersonMutation = useMutation({
    mutationFn: async (id: string) => {
      await fetch(`/api/people/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
    }
  });

  return { 
    data,
    isLoading,
    error,
    updatePerson: updatePersonMutation,
    deletePerson: deletePersonMutation
  } as const;
}

// Backward compatibility for existing components
export function useBitcoiners() {
  const { data, isLoading, error } = usePeople();
  return {
    data,
    bitcoiners: data,
    isLoading,
    error
  };
}